import React, { useState, useEffect } from 'react';
import { CubeIcon } from '@heroicons/react/24/outline';
import { treatmentApi, CreateTreatmentRecordDto } from '../../api/treatments';
import { healthTestsApi } from '../../api/healthTests';
import { getBirds } from '../../api/birds';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';
import AmountInput from '../ui/AmountInput';

interface Bird {
  id: string;
  bandId: string;
  species?: { commonName: string };
}

interface HealthTest {
  id: string;
  testName: string;
  testDate: string;
}

interface AddTreatmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTreatmentRecordDto) => Promise<void>;
  birdId?: string;
  healthTestId?: string;
  birds?: Bird[];
}

const AddTreatmentModal: React.FC<AddTreatmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  birdId,
  healthTestId,
  birds = [],
}) => {
  const [formData, setFormData] = useState<CreateTreatmentRecordDto>({
    birdId: birdId || '',
    healthTestId: healthTestId || '',
    treatmentName: '',
    treatmentType: 'MEDICATION',
    medicationName: '',
    activeIngredient: '',
    dosage: '',
    frequency: 'ONCE_DAILY',
    administrationRoute: 'ORAL',
    startDate: '',
    endDate: '',
    prescribedBy: '',
    veterinarian: '',
    clinicName: '',
    indication: '',
    instructions: '',
    sideEffects: '',
    contraindications: '',
    totalDoses: 0,
    notes: '',
    cost: 0,
  });
  const [availableBirds, setAvailableBirds] = useState<Bird[]>([]);
  const [healthTests, setHealthTests] = useState<HealthTest[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Use provided birds or load them if not provided
      if (Array.isArray(birds) && birds.length > 0) {
        setAvailableBirds(birds);
      } else {
        loadBirds();
      }
      
      setFormData(prev => ({
        ...prev,
        birdId: birdId || prev.birdId,
        healthTestId: healthTestId || prev.healthTestId,
        startDate: new Date().toISOString().split('T')[0],
      }));
    }
  }, [isOpen, birdId, healthTestId, birds]);

  const loadBirds = async () => {
    try {
      setLoading(true);
      const response = await getBirds();
      setAvailableBirds(response);
    } catch (error) {
      console.error('Error loading birds:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBirdHealthTests = async (selectedBirdId: string) => {
    try {
      const data = await healthTestsApi.getByBirdId(selectedBirdId);
      setHealthTests(data);
    } catch (error) {
      console.error('Error loading health tests:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'birdId' && value !== formData.birdId) {
      loadBirdHealthTests(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        healthTestId: '', // Reset health test selection when bird changes
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'cost' || name === 'totalDoses' ? parseFloat(value) || 0 : value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.birdId || !formData.treatmentName || !formData.indication || !formData.prescribedBy) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit(formData);
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating treatment:', error);
      alert('Failed to create treatment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      birdId: birdId || '',
      healthTestId: healthTestId || '',
      treatmentName: '',
      treatmentType: 'MEDICATION',
      medicationName: '',
      activeIngredient: '',
      dosage: '',
      frequency: 'ONCE_DAILY',
      administrationRoute: 'ORAL',
      startDate: '',
      endDate: '',
      prescribedBy: '',
      veterinarian: '',
      clinicName: '',
      indication: '',
      instructions: '',
      sideEffects: '',
      contraindications: '',
      totalDoses: 0,
      notes: '',
      cost: 0,
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center">
            <CubeIcon className="h-6 w-6 text-purple-500 mr-2" />
            Add Treatment
          </span>
        }
        onClose={handleClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-4 max-h-96 overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Bird Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Bird *
              </label>              <select
                name="birdId"
                value={formData.birdId}
                onChange={handleInputChange}
                required
                disabled={!!birdId || loading}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="empty-option-1753461297319" value="">Select a bird...</option>
                <option key="ALL_BIRDS-1753461297319" value="ALL_BIRDS" className="font-medium text-cyan-600">
                  🔵 All Birds / Flock
                </option>
                {Array.isArray(availableBirds) && availableBirds.map((bird) => (
                  <option key={bird.id} value={bird.id}>
                    {bird.bandId} {bird.species?.commonName ? `(${bird.species.commonName})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Health Test Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Related Health Test
              </label>
              <select
                name="healthTestId"
                value={formData.healthTestId}
                onChange={handleInputChange}
                disabled={!formData.birdId}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="empty-option-1753461297319" value="">Select a health test (optional)...</option>
                {Array.isArray(healthTests) && healthTests.map((test) => (
                  <option key={test.id} value={test.id}>
                    {test.testName} ({new Date(test.testDate).toLocaleDateString()})
                  </option>
                ))}
              </select>
            </div>

            {/* Treatment Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Treatment Name *
              </label>
              <input
                type="text"
                name="treatmentName"
                value={formData.treatmentName}
                onChange={handleInputChange}
                required
                placeholder="e.g., Antibiotic Treatment"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Treatment Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Treatment Type *
              </label>
              <select
                name="treatmentType"
                value={formData.treatmentType}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="MEDICATION-1753461297319" value="MEDICATION">Medication</option>
                <option key="INJECTION-1753461297319" value="INJECTION">Injection</option>
                <option key="TOPICAL-1753461297319" value="TOPICAL">Topical</option>
                <option key="ORAL-1753461297319" value="ORAL">Oral</option>
                <option key="SURGERY-1753461297319" value="SURGERY">Surgery</option>
                <option key="THERAPY-1753461297319" value="THERAPY">Therapy</option>
                <option key="SUPPLEMENT-1753461297319" value="SUPPLEMENT">Supplement</option>
              </select>
            </div>

            {/* Medication Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Medication Name
              </label>
              <input
                type="text"
                name="medicationName"
                value={formData.medicationName}
                onChange={handleInputChange}
                placeholder="e.g., Amoxicillin"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Active Ingredient */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Active Ingredient
              </label>
              <input
                type="text"
                name="activeIngredient"
                value={formData.activeIngredient}
                onChange={handleInputChange}
                placeholder="e.g., Amoxicillin trihydrate"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Dosage */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Dosage *
              </label>
              <input
                type="text"
                name="dosage"
                value={formData.dosage}
                onChange={handleInputChange}
                required
                placeholder="e.g., 10mg/kg"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Frequency *
              </label>
              <select
                name="frequency"
                value={formData.frequency}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="ONCE_DAILY-1753461297319" value="ONCE_DAILY">Once Daily</option>
                <option key="TWICE_DAILY-1753461297319" value="TWICE_DAILY">Twice Daily</option>
                <option key="THREE_TIMES_DAILY-1753461297319" value="THREE_TIMES_DAILY">Three Times Daily</option>
                <option key="EVERY_6_HOURS-1753461297319" value="EVERY_6_HOURS">Every 6 Hours</option>
                <option key="EVERY_8_HOURS-1753461297319" value="EVERY_8_HOURS">Every 8 Hours</option>
                <option key="EVERY_12_HOURS-1753461297319" value="EVERY_12_HOURS">Every 12 Hours</option>
                <option key="WEEKLY-1753461297319" value="WEEKLY">Weekly</option>
                <option key="AS_NEEDED-1753461297319" value="AS_NEEDED">As Needed</option>
                <option key="SINGLE_DOSE-1753461297319" value="SINGLE_DOSE">Single Dose</option>
              </select>
            </div>

            {/* Administration Route */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Administration Route *
              </label>
              <select
                name="administrationRoute"
                value={formData.administrationRoute}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="ORAL-1753461297319" value="ORAL">Oral</option>
                <option key="INTRAMUSCULAR-1753461297319" value="INTRAMUSCULAR">Intramuscular</option>
                <option key="SUBCUTANEOUS-1753461297319" value="SUBCUTANEOUS">Subcutaneous</option>
                <option key="INTRAVENOUS-1753461297319" value="INTRAVENOUS">Intravenous</option>
                <option key="TOPICAL-1753461297319" value="TOPICAL">Topical</option>
                <option key="INHALATION-1753461297319" value="INHALATION">Inhalation</option>
                <option key="OCULAR-1753461297319" value="OCULAR">Ocular</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Start Date *
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Prescribed By */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Prescribed By *
              </label>
              <input
                type="text"
                name="prescribedBy"
                value={formData.prescribedBy}
                onChange={handleInputChange}
                required
                placeholder="Dr. Smith"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Veterinarian */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Veterinarian
              </label>
              <input
                type="text"
                name="veterinarian"
                value={formData.veterinarian}
                onChange={handleInputChange}
                placeholder="Dr. Johnson"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Clinic Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Clinic Name
              </label>
              <input
                type="text"
                name="clinicName"
                value={formData.clinicName}
                onChange={handleInputChange}
                placeholder="Avian Veterinary Clinic"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Total Doses */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Total Doses
              </label>
              <input
                type="number"
                name="totalDoses"
                value={formData.totalDoses}
                onChange={handleInputChange}
                min="0"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Cost */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Cost ($)
              </label>
              <AmountInput
                name="cost"
                value={formData.cost}
                onChange={(v) => setFormData(prev => ({ ...prev, cost: v ?? 0 }))}
                min={0}
                inputClassName="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Indication */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Indication *
              </label>
              <input
                type="text"
                name="indication"
                value={formData.indication}
                onChange={handleInputChange}
                required
                placeholder="e.g., Respiratory infection, Feather plucking"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Instructions */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Instructions
              </label>
              <textarea
                name="instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                rows={2}
                placeholder="Detailed administration instructions..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Side Effects */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Potential Side Effects
              </label>
              <textarea
                name="sideEffects"
                value={formData.sideEffects}
                onChange={handleInputChange}
                rows={2}
                placeholder="Known side effects to watch for..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Contraindications */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Contraindications
              </label>
              <textarea
                name="contraindications"
                value={formData.contraindications}
                onChange={handleInputChange}
                rows={2}
                placeholder="Conditions where this treatment should not be used..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Notes */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Additional notes about the treatment..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {submitting ? 'Adding...' : 'Add Treatment'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default AddTreatmentModal; 

